import { useEffect, useMemo, useState } from 'react';
import {
  Content,
  EmptyState,
  Header,
  Link,
  Page,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import PersonIcon from '@material-ui/icons/Person';
import GroupIcon from '@material-ui/icons/Group';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import GitHubIcon from '@material-ui/icons/GitHub';
import EditIcon from '@material-ui/icons/Edit';
import Alert from '@material-ui/lab/Alert';
import { UserManagementClient } from './api';
import {
  EditGithubUsernameDialog,
  UserRowData,
} from './popup';
import { catalogUserUpdatePermission } from './permissions';

const useStyles = makeStyles(theme => ({
  filterPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
  },
  chipGroup: {
    margin: theme.spacing(0.25),
    cursor: 'pointer',
  },
  statCard: {
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
  },
  userNameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  userAvatar: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    borderRadius: '50%',
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  githubLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}));

interface CatalogEntity {
  apiVersion: string;
  kind: string;
  metadata: {
    name: string;
    namespace?: string;
    title?: string;
    description?: string;
    annotations?: Record<string, string>;
  };
  spec?: {
    profile?: {
      displayName?: string;
      email?: string;
    };
    memberOf?: string[];
  };
}

interface GroupSummary {
  name: string;
  displayName: string;
  namespace: string;
}

export function UsersPage() {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const client = useMemo(
    () => new UserManagementClient(discoveryApi, fetchApi),
    [discoveryApi, fetchApi],
  );

  // This controls UI visibility; the backend independently authorizes every update.
  const { allowed: canEditMapping } = usePermission({
    permission: catalogUserUpdatePermission,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [data, setData] = useState<{
    users: UserRowData[];
    groups: GroupSummary[];
  }>({ users: [], groups: [] });

  // Optimistic local overrides to prevent username flickering before Catalog sync completes
  const [savedOverrides, setSavedOverrides] = useState<
    Record<string, string | null>
  >({});
  const [selectedUserForEdit, setSelectedUserForEdit] =
    useState<UserRowData | null>(null);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(undefined);

    Promise.all([
      catalogApi.getEntities({ filter: { kind: 'user' } }),
      catalogApi.getEntities({ filter: { kind: 'group' } }),
      client.getGithubUsernames(),
    ])
      .then(([usersResponse, groupsResponse, mappingsResponse]) => {
        if (!mounted) return;

        // Read persisted mappings for all catalog sources, including file-based Users.
        const mappings = new Map(
          mappingsResponse.items.map(item => [item.entityRef, item.githubUsername]),
        );

        const users: UserRowData[] = (
          usersResponse.items as CatalogEntity[]
        ).map(user => {
          const namespace = user.metadata.namespace || 'default';
          const name = user.metadata.name;
          const displayName = user.spec?.profile?.displayName || name;
          const email = user.spec?.profile?.email;
          const memberOf = user.spec?.memberOf || [];
          const entityRef = `user:${namespace}/${name}`;
          const githubUsername = mappings.has(entityRef)
            ? mappings.get(entityRef) ?? undefined
            : user.metadata.annotations?.['github.com/user-login'];

          return {
            name,
            namespace,
            displayName,
            email,
            memberOf,
            entityRef: `user:${namespace}/${name}`,
            profileUrl: `/catalog/${namespace}/user/${name}`,
            githubUsername,
          };
        });

        const groups: GroupSummary[] = (
          groupsResponse.items as CatalogEntity[]
        ).map(group => ({
          name: group.metadata.name,
          displayName:
            group.spec?.profile?.displayName ||
            group.metadata.title ||
            group.metadata.name,
          namespace: group.metadata.namespace || 'default',
        }));

        setData({ users, groups });
        setLoading(false);
      })
      .catch(err => {
        if (!mounted) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [catalogApi, client]);

  const allGroups = useMemo(() => {
    return data.groups;
  }, [data.groups]);

  const filteredUsers = useMemo(() => {
    return data.users.filter(user => {
      // Group filter
      if (selectedGroup !== 'all') {
        const isInGroup = user.memberOf.some(g => {
          const groupName = g.includes('/') ? g.split('/').pop() : g;
          return groupName?.toLowerCase() === selectedGroup.toLowerCase();
        });
        if (!isInGroup) return false;
      }

      // Search term filter
      if (searchTerm.trim() !== '') {
        const query = searchTerm.toLowerCase().trim();
        const matchesName = user.name.toLowerCase().includes(query);
        const matchesDisplay = user.displayName.toLowerCase().includes(query);
        const matchesEmail = user.email?.toLowerCase().includes(query) ?? false;
        const matchesGroup = user.memberOf.some(g =>
          g.toLowerCase().includes(query),
        );
        const effectiveGithub =
          user.entityRef in savedOverrides
            ? savedOverrides[user.entityRef]
            : user.githubUsername;
        const matchesGithub =
          effectiveGithub?.toLowerCase().includes(query) ?? false;

        return (
          matchesName ||
          matchesDisplay ||
          matchesEmail ||
          matchesGroup ||
          matchesGithub
        );
      }

      return true;
    });
  }, [data.users, selectedGroup, searchTerm, savedOverrides]);

  const handleEditSuccess = (updated: {
    entityRef: string;
    githubUsername: string | null;
    email: string;
  }) => {
    setSavedOverrides(prev => ({
      ...prev,
      [updated.entityRef]: updated.githubUsername,
    }));

    setSuccessNotice(
      `Saved GitHub username "${updated.githubUsername || '(removed)'
      }" for ${updated.entityRef}. Catalog will complete synchronization in the background.`,
    );
  };

  const columns: TableColumn<UserRowData>[] = [
    {
      title: 'User',
      field: 'displayName',
      render: row => (
        <Box className={classes.userNameCell}>
          <div className={classes.userAvatar}>
            <PersonIcon fontSize="small" />
          </div>
          <Box>
            <Typography variant="subtitle2">
              <Link to={row.profileUrl}>{row.displayName}</Link>
            </Typography>
            {row.displayName !== row.name && (
              <Typography variant="caption" color="textSecondary">
                {row.name}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      title: 'Email',
      field: 'email',
      render: row =>
        row.email ? (
          <Link to={`mailto:${row.email}`}>{row.email}</Link>
        ) : (
          <Typography variant="body2" color="textSecondary">
            —
          </Typography>
        ),
    },
    {
      title: 'Groups',
      field: 'memberOf',
      render: row =>
        row.memberOf.length > 0 ? (
          <Box display="flex" flexWrap="wrap" alignItems="center">
            {row.memberOf.map(group => {
              const groupName = group.includes('/')
                ? group.split('/').pop()!
                : group;
              return (
                <Link
                  key={group}
                  to={`/catalog/${row.namespace}/group/${groupName}`}
                >
                  <Chip
                    size="small"
                    label={groupName}
                    className={classes.chipGroup}
                    clickable
                    variant="outlined"
                    color="primary"
                  />
                </Link>
              );
            })}
          </Box>
        ) : (
          <Typography variant="caption" color="textSecondary">
            No group
          </Typography>
        ),
    },

    // {
    //   title: 'Entity Ref',
    //   field: 'entityRef',
    //   render: row => (
    //     <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
    //       {row.entityRef}
    //     </Typography>
    //   ),
    // },
    {
      title: 'GitHub Username',
      render: row => {
        const effectiveUsername =
          row.entityRef in savedOverrides
            ? savedOverrides[row.entityRef]
            : row.githubUsername;

        if (!effectiveUsername) {
          return (
            <Typography variant="body2" color="textSecondary">
              —
            </Typography>
          );
        }

        return (
          <Link
            to={`https://github.com/${encodeURIComponent(effectiveUsername)}`}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.githubLink}
          >
            <GitHubIcon fontSize="small" />
            <span>{effectiveUsername}</span>
          </Link>
        );
      },
    },
    {
      title: 'Actions',
      render: row => (
        <Box className={classes.actionCell}>
          <Link to={row.profileUrl}>
            <Box display="flex" alignItems="center" gridGap={4}>
              <span>View Profile</span>
              <OpenInNewIcon fontSize="inherit" />
            </Box>
          </Link>
          {canEditMapping && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => setSelectedUserForEdit(row)}
            >
              Edit
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Page themeId="home">
      <Header
        title="Users"
        subtitle="Manage and look up organization user profiles"
      />
      <Content>
        {successNotice && (
          <Box mb={2}>
            <Alert severity="success" onClose={() => setSuccessNotice(null)}>
              {successNotice}
            </Alert>
          </Box>
        )}

        {loading && <Progress />}

        {error && <ResponseErrorPanel error={error} />}

        {!loading && !error && (
          <>
            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper className={classes.statCard} variant="outlined">
                  <PersonIcon color="primary" />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Total Users
                    </Typography>
                    <Typography variant="h6">{data.users.length}</Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper className={classes.statCard} variant="outlined">
                  <GroupIcon color="primary" />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Total Groups
                    </Typography>
                    <Typography variant="h6">{data.groups.length}</Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            <Paper className={classes.filterPaper} variant="outlined">
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={8} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Search by name, display name, email, GitHub, or group..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    select
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Filter by Group"
                    value={selectedGroup}
                    onChange={e => setSelectedGroup(e.target.value)}
                  >
                    <MenuItem value="all">All Groups</MenuItem>
                    {allGroups.map(group => (
                      <MenuItem key={group.name} value={group.name}>
                        {group.displayName} ({group.name})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {(searchTerm || selectedGroup !== 'all') && (
                  <Grid item xs={12} sm={12} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      Showing {filteredUsers.length} of {data.users.length} users
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {filteredUsers.length === 0 ? (
              <EmptyState
                missing="data"
                title="No Users Found"
                description={
                  searchTerm || selectedGroup !== 'all'
                    ? 'No users match the current filter. Try changing your search query or selecting a different group.'
                    : 'No users currently registered in the Catalog.'
                }
              />
            ) : (
              <Table
                title="Organization Users"
                options={{
                  search: false,
                  paging: true,
                  pageSize: 10,
                  pageSizeOptions: [10, 20, 50],
                }}
                columns={columns}
                data={filteredUsers}
              />
            )}
          </>
        )}

        <EditGithubUsernameDialog
          open={Boolean(selectedUserForEdit)}
          user={selectedUserForEdit}
          onClose={() => setSelectedUserForEdit(null)}
          onSuccess={handleEditSuccess}
          client={client}
        />
      </Content>
    </Page>
  );
}
